import { shouldDropzoneAcceptDrag } from './file-list-helpers';

/**
 * FRONT-2434: the Files tab takes a desktop file anywhere over it, not only on
 * a dashed box. While a file from the computer is over the tab an overlay says
 * where it will land; at rest there is nothing to see.
 *
 * Same trap as the old box (FRONT-776): only a real OS file drag may arm it.
 * A row being dragged toward a folder never carries `'Files'`, so it never
 * lights the overlay and never calls `preventDefault()` (which would make the
 * browser treat the region as a drop target for it).
 *
 * `dragenter`/`dragleave` bubble from every child boundary the pointer
 * crosses, so the overlay clears only when the net count returns to zero —
 * the pointer actually left the region. Pure and exported so the guard and
 * the counting run under the node-only unit suite, which can't dispatch a
 * real drag.
 */
export interface FileDropEventLike {
  dataTransfer?: { types?: readonly string[] | null; files?: FileList | null; dropEffect?: string } | null;
  preventDefault: () => void;
}

export interface FileDropRegionHandlers {
  onDragEnter: (e: FileDropEventLike) => void;
  onDragOver: (e: FileDropEventLike) => void;
  onDragLeave: (e: FileDropEventLike) => void;
  onDrop: (e: FileDropEventLike) => void;
}

export function buildFileDropRegionHandlers(params: {
  /** Off on tabs that don't take uploads (the recycle bin, the gallery tabs). */
  enabled: boolean;
  /** Net enters minus leaves, kept by the caller across renders. */
  depth: { current: number };
  setOver: (over: boolean) => void;
  onFiles: (files: FileList | null | undefined) => void;
}): FileDropRegionHandlers {
  const { enabled, depth, setOver, onFiles } = params;
  const accepts = (e: FileDropEventLike) => enabled && shouldDropzoneAcceptDrag(e.dataTransfer);
  return {
    onDragEnter: (e) => {
      if (!accepts(e)) return;
      e.preventDefault();
      depth.current += 1;
      setOver(true);
    },
    onDragOver: (e) => {
      if (!accepts(e)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    },
    onDragLeave: (e) => {
      if (!accepts(e)) return;
      depth.current = Math.max(0, depth.current - 1);
      if (depth.current === 0) setOver(false);
    },
    onDrop: (e) => {
      if (!accepts(e)) return;
      e.preventDefault();
      depth.current = 0;
      setOver(false);
      onFiles(e.dataTransfer?.files);
    },
  };
}
